import {
  Scene, Mesh, CylinderGeometry, MeshBasicMaterial,
  Object3D, Vector3, Color,
} from 'three';

/** 飛行機雲の1パーティクル */
interface TrailPoint {
  mesh: Mesh;
  life: number;
  maxLife: number;
}

/** エンジン出力状態 */
export interface EngineState {
  boost: boolean;
  accel: boolean;
  decel: boolean;
  speedKmh: number;
}

const INITIAL_RADIUS  = 0.3;

// アフターバーナー設定
const AB_LIFE         = 0.6;
const AB_SPAWN_INT    = 0.015;
const AB_OPACITY      = 0.5;
const AB_COLOR        = new Color(0xffaa44);
const AB_SCALE_MAX    = 3.0;

// 飛行機雲設定（高速・高高度）
const CONTRAIL_LIFE   = 2.5;
const CONTRAIL_SPAWN  = 0.03;
const CONTRAIL_MIN_SPD = 600; // km/h
const CONTRAIL_MIN_ALT = 500; // m
const CONTRAIL_OPACITY = 0.25;
const CONTRAIL_COLOR   = new Color(0xffffff);
const CONTRAIL_SCALE   = 5.0;

/**
 * エンジン排気・飛行機雲エフェクト
 *
 * - ブースト(Shift): オレンジのアフターバーナー炎
 * - 加速(W) + 高速: 薄い排気
 * - 高速+高高度: 白い飛行機雲（コントレイル）
 * - 減速(S) / 低速: エフェクトなし
 */
export class TrailEffect {
  private points: TrailPoint[] = [];
  private abTimer = 0;
  private contrailTimer = 0;
  private geoSmall = new CylinderGeometry(INITIAL_RADIUS, INITIAL_RADIUS * 1.5, 1.2, 5);
  private geoLarge = new CylinderGeometry(INITIAL_RADIUS, INITIAL_RADIUS, 1.8, 5);

  constructor(private scene: Scene) {}

  update(target: Object3D, engine: EngineState, delta: number): void {
    // 既存パーティクル更新
    for (let i = this.points.length - 1; i >= 0; i--) {
      const p = this.points[i];
      p.life -= delta;
      const t = 1 - p.life / p.maxLife;
      const mat = p.mesh.material as MeshBasicMaterial;
      const baseOpacity = mat.userData.baseOpacity as number;
      const maxScale = mat.userData.maxScale as number;
      mat.opacity = Math.max(0, baseOpacity * (1 - t * t));
      const s = 1 + t * (maxScale - 1);
      p.mesh.scale.set(s, 1, s);

      if (p.life <= 0) {
        this.scene.remove(p.mesh);
        mat.dispose();
        this.points.splice(i, 1);
      }
    }

    const alt = target.position.y;

    // アフターバーナー（ブースト中のみ）
    if (engine.boost) {
      this.abTimer -= delta;
      if (this.abTimer <= 0) {
        this.abTimer = AB_SPAWN_INT;
        this.spawnNozzleTrail(target, AB_COLOR, AB_OPACITY, AB_LIFE, AB_SCALE_MAX, this.geoSmall);
      }
    } else {
      this.abTimer = 0;
    }

    // 加速中の薄い排気
    if (engine.accel && !engine.boost && engine.speedKmh > 400) {
      this.abTimer -= delta;
      if (this.abTimer <= 0) {
        this.abTimer = 0.04;
        this.spawnNozzleTrail(
          target, new Color(0xcccccc), 0.15, 0.4, 2.0, this.geoSmall
        );
      }
    }

    // 高速+高高度のコントレイル
    if (engine.speedKmh >= CONTRAIL_MIN_SPD && alt >= CONTRAIL_MIN_ALT && !engine.decel) {
      this.contrailTimer -= delta;
      if (this.contrailTimer <= 0) {
        this.contrailTimer = CONTRAIL_SPAWN;
        this.spawnNozzleTrail(
          target, CONTRAIL_COLOR, CONTRAIL_OPACITY, CONTRAIL_LIFE, CONTRAIL_SCALE, this.geoLarge
        );
      }
    } else {
      this.contrailTimer = 0;
    }
  }

  private spawnNozzleTrail(
    target: Object3D, color: Color, opacity: number,
    life: number, maxScale: number, geo: CylinderGeometry,
  ): void {
    for (const sx of [-0.88, 0.88]) {
      const offset = new Vector3(sx, -0.06, 8.2);
      offset.applyQuaternion(target.quaternion);
      const worldPos = target.position.clone().add(offset);

      const mat = new MeshBasicMaterial({
        color, transparent: true, opacity, depthWrite: false,
      });
      mat.userData.baseOpacity = opacity;
      mat.userData.maxScale = maxScale;
      const mesh = new Mesh(geo, mat);
      mesh.position.copy(worldPos);
      mesh.quaternion.copy(target.quaternion);
      this.scene.add(mesh);
      this.points.push({ mesh, life, maxLife: life });
    }
  }

  dispose(): void {
    for (const p of this.points) {
      this.scene.remove(p.mesh);
      (p.mesh.material as MeshBasicMaterial).dispose();
    }
    this.points = [];
  }
}
